import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@xcentral/ui';
import { getAppInsights } from '../appInsights';

/**
 * Page not found — Bug #22185 (BR-MOB-014, BR-MOB-015; operator decisions A1, A2 of 2026-10-02).
 *
 * Rendered by the `path="*"` catch-all INSIDE the AdminLayout route in App.tsx, so an unknown admin
 * route keeps the normal app shell (top bar + navigation) instead of rendering a blank page.
 *
 * Not for a record-level 404 inside a known route (e.g. `/accounts/<unknown id>`): that route still
 * matches its own page, which keeps its own not-found state.
 *
 * Telemetry (BR-MOB-015): each arrival on this page logs one App Insights trace at Warning severity,
 * `admin.route.not-found`. Only the PATH is logged, with every GUID or all-digit segment replaced by
 * `{id}` (A2) — never the query string, never a raw id. The path is the only property logged.
 * Nothing about the logging is shown to the user.
 */

/** The App Insights event name for an unmatched admin route. */
export const NOT_FOUND_TRACE = 'admin.route.not-found';

/** `SeverityLevel.Warning` in @microsoft/applicationinsights-web. A literal, so this page does not
 *  pull the lazily-loaded SDK into the main bundle (see appInsights.ts). */
const SEVERITY_WARNING = 2;

const GUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGIT_SEGMENT = /^\d+$/;

/**
 * Mask record ids in a path for telemetry: every GUID or all-digit segment becomes `{id}`. Any query
 * string or hash is dropped. `/sign/envelopes/<guid>/x?t=1` → `/sign/envelopes/{id}/x`.
 */
export function maskPathIds(path: string): string {
  const bare = path.split(/[?#]/, 1)[0] ?? '';
  return bare
    .split('/')
    .map((segment) => {
      let decoded = segment;
      try {
        decoded = decodeURIComponent(segment);
      } catch {
        // A malformed escape is not an id; keep the raw segment.
      }
      return GUID_SEGMENT.test(decoded) || DIGIT_SEGMENT.test(decoded) ? '{id}' : segment;
    })
    .join('/');
}

/**
 * True when there is an in-app history entry to go Back to. react-router's BrowserRouter stores the
 * entry index in `window.history.state.idx` (0 = the first in-app entry: a direct load or a pasted
 * link); a MemoryRouter does not touch `window.history`, so a non-initial `location.key` covers it.
 */
function hasInAppHistory(locationKey: string): boolean {
  const state: unknown = typeof window === 'undefined' ? null : window.history.state;
  const idx = state && typeof state === 'object' && 'idx' in state ? (state as { idx: unknown }).idx : undefined;
  if (typeof idx === 'number') return idx > 0;
  return locationKey !== 'default';
}

export function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = hasInAppHistory(location.key);

  // One trace per arrival. Keyed on location.key so a StrictMode double-invoked effect does not
  // log twice, while a navigation to another unknown path logs again.
  const loggedKey = useRef<string | null>(null);
  useEffect(() => {
    if (loggedKey.current === location.key) return;
    loggedKey.current = location.key;
    try {
      getAppInsights()?.trackTrace(
        { message: NOT_FOUND_TRACE, severityLevel: SEVERITY_WARNING },
        { path: maskPathIds(location.pathname) },
      );
    } catch {
      // Telemetry must never break the page (BR-MOB-015: nothing about the logging is shown).
    }
  }, [location.key, location.pathname]);

  return (
    <div className="mx-auto w-full max-w-2xl">
      <h1 className="text-2xl font-bold tracking-tight text-gray-900">Page not found</h1>
      <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-5 shadow-sm sm:px-6">
        <p className="text-base font-semibold text-gray-900">We couldn&rsquo;t find that page.</p>
        <p className="mt-1 text-sm text-gray-600">
          There is no admin page at this address. It may have moved, or the link may be wrong.
        </p>
        <div className="mt-4 flex flex-col gap-1 md:flex-row md:items-baseline md:gap-2">
          <span className="text-xs text-gray-500">Requested path</span>
          <code data-testid="not-found-path" className="min-w-0 break-all text-sm text-gray-900">
            {location.pathname}
          </code>
        </div>
        <div data-testid="not-found-actions" className="mt-6 flex flex-col gap-2 md:flex-row">
          <Button className="w-full md:w-auto" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
          {showBack && (
            <Button variant="secondary" className="w-full md:w-auto" onClick={() => navigate(-1)}>
              Back
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotFound;
